import React, { useEffect } from 'react';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { ucwords } from '../helper';
import useCalculationItemHeaderForm from '../hooks/useCalculationItemHeaderForm';
import { INITIAL_COMPUTATION } from '../constant';

function CalculationItemHeader({ permitId, initialFeeId, data }) {
  const isOption =
    ['OPTION'].indexOf(get(data, 'included.source.attributes.type')) > -1;

  const inititalFormData = {};

  const rules = {
    [get(data, 'attributes.property')]: 'required|number',
  };

  const {
    formData,
    onChangeDecimal,
    onBlurDecimal,
    onChangeSelect,
    formValidator,
    onUpdateCalculation,
    updateListReducer,
    onGetCalculation,
    setFormData,
  } = useCalculationItemHeaderForm(inititalFormData, rules);

  const isOtherFee = get(data, 'attributes.source_type') === 'BPLS_OTHER_FEE';
  const isFixedFee = get(data, 'attributes.source_type') === 'BPLS_FIXED_FEE';
  const isExcessCalculation =
    get(data, 'attributes.source_type') === 'BPLS_EXCESS_CALCULATION';

  const sourceDetails = get(data, 'included.sourceDetails', []);

  const options =
    sourceDetails.length > 0 &&
    get(sourceDetails, '0.type') === 'bpls_tax_revenue_calculation_options'
      ? sourceDetails.map(item => {
          return {
            label: get(item, 'attributes.multiplier'),
            value: get(item, 'attributes.multiplier'),
          };
        })
      : [];

  useEffect(() => {
    formValidator.onSetObjectRules({
      [get(data, 'attributes.property')]: 'required',
    });
  }, [isOption]);

  const setInitialFormData = () => {
    if ((isOtherFee || isFixedFee) && !get(data, 'attributes.property')) {
      return setFormData({
        ...formData,
        amount: !isOption
          ? parseFloat(get(data, 'attributes.amount') || 0, 10)
          : get(data, 'attributes.amount'),
      });
    }

    return setFormData({
      ...formData,
      [get(data, 'attributes.property')]: !isOption
        ? parseFloat(get(data, 'attributes.value') || 0, 10)
        : get(data, 'attributes.value'),
    });
  };

  const getInputProps = () => {
    if ((isOtherFee || isFixedFee) && !get(data, 'attributes.property')) {
      return {
        name: 'amount',
        value: get(formData, `amount`),
      };
    }

    return {
      name: get(data, 'attributes.property'),
      value: get(formData, `${get(data, 'attributes.property')}`),
    };
  };

  useEffect(() => {
    setInitialFormData();
  }, [
    get(data, 'id'),
    isOtherFee,
    isFixedFee,
    isOption,
    get(data, 'attributes.value'),
  ]);

  const onKeyDown = e => {
    if (e.keyCode === 13) {
      onUpdateCalculation(
        permitId,
        initialFeeId,
        get(data, 'id'),
        formData,
        res => {
          updateListReducer(`${INITIAL_COMPUTATION}`, get(res, 'res.data'));
          onGetCalculation(permitId, initialFeeId);
        }
      );
    }
  };

  const onChangeSelectCallBack = payload => {
    onUpdateCalculation(
      permitId,
      initialFeeId,
      get(data, 'id'),
      payload,
      res => {
        updateListReducer(`${INITIAL_COMPUTATION}`, get(res, 'res.data'));
        onGetCalculation(permitId, initialFeeId);
      }
    );
  };

  const inputProps = getInputProps();

  return (
    <table className="table table-borderless table-sm mb-0">
      <tbody>
        <tr>
          <td
            className="text-dark"
            width="150"
            style={{ verticalAlign: 'middle' }}
          >
            {ucwords(
              (get(data, 'attributes.property') || 'Amount').replace(/_/g, ' ')
            )}
          </td>
          <td
            className="text-dark"
            width="1"
            style={{ verticalAlign: 'middle' }}
          >
            :
          </td>
          <td colSpan={20} width="150">
            {!isOption && (
              <div className="d-flex">
                <div className="w-50">
                  <input
                    disabled={!get(data, 'attributes.property') && isFixedFee}
                    onKeyDown={onKeyDown}
                    onChange={onChangeDecimal}
                    onBlur={onBlurDecimal}
                    name={inputProps.name}
                    type="text"
                    className="form-control"
                    value={inputProps.value}
                  />
                </div>
                &nbsp;
                <div className="w-50 d-flex align-items-center">
                  {formValidator.renderMessage(
                    `${get(data, 'attributes.property')}`
                  )}
                </div>
              </div>
            )}
            {isOption && (
              <div className="d-flex">
                <div className="w-50">
                  <Select
                    disabled={!get(data, 'attributes.property')}
                    options={options}
                    onChange={onChangeSelect(
                      get(data, 'attributes.property'),
                      onChangeSelectCallBack
                    )}
                    value={
                      options.find(
                        x =>
                          x.value ===
                          get(formData, `${get(data, 'attributes.property')}`)
                      ) || null
                    }
                  />
                </div>
                &nbsp;
                <div className="w-50 d-flex align-items-center">
                  {formValidator.renderMessage(
                    `${get(data, 'attributes.property')}`
                  )}
                </div>
              </div>
            )}
          </td>
        </tr>
        <tr>
          {get(data, 'included.source.attributes.rate') && !isOtherFee ? (
            <>
              <td className="text-dark" width="150">
                Rate
              </td>
              <td className="text-dark" width="1">
                :
              </td>
              <td colSpan={4} width="150">
                <b>{get(data, 'included.source.attributes.rate') || '-'}</b>
              </td>
            </>
          ) : null}
          {get(data, 'included.source.attributes.amount_human') &&
          !isOtherFee &&
          !isExcessCalculation ? (
            <>
              <td className="text-dark" width="150">
                Amount
              </td>
              <td className="text-dark" width="1">
                :
              </td>
              <td colSpan={4} width="150">
                <b>
                  {get(data, 'included.source.attributes.amount_human') || '-'}
                </b>
              </td>
            </>
          ) : null}
          {get(data, 'attributes.amount_human') && !isOtherFee ? (
            <>
              <td className="text-dark" width="150">
                Calculated
              </td>
              <td className="text-dark" width="1">
                :
              </td>
              <td colSpan={4} width="150">
                <b>{get(data, 'attributes.amount_human') || '-'}</b>
              </td>
            </>
          ) : null}
        </tr>
      </tbody>
    </table>
  );
}

CalculationItemHeader.propTypes = {
  data: PropTypes.instanceOf(Array).isRequired,
  permitId: PropTypes.number.isRequired,
  initialFeeId: PropTypes.number.isRequired,
};

export default CalculationItemHeader;
